import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { CheckCircle, ChevronRight, Mail, Sparkles, Star, Diamond, Gem, Crown, BookOpen, Wrench, Server, Code, HardDrive, Shield, Award, Zap, Cloud } from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/useToast';
import { sendElearningOrderConfirmationEmail } from '../../services/elearningEmailService';
import { supabase } from '../../lib/supabase';
import { useTenantContext } from '../../context/TenantContext';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const fadeIn = { 
  hidden: { opacity: 0, y: 20 }, 
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
};

const ElearningConfirmationPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();

  // Thème E-learning (preview > tenant)
  const elearningTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  
  // Récupérer le paramètre de l'URL ou du localStorage
  useEffect(() => {
    // Récupérer le code de commande depuis l'URL ou localStorage
    const storedOrderId = localStorage.getItem('last_elearning_order_id');
    
    let resolvedOrderId: string | null = null;
    if (orderId) {
      resolvedOrderId = orderId;
      localStorage.setItem('last_elearning_order_id', orderId);
    } else if (storedOrderId) {
      resolvedOrderId = storedOrderId;
    }
    
    // Vérifier si l'email a déjà été envoyé pour cette commande
    const emailSentKey = `elearning_email_sent_${resolvedOrderId}`;
    const alreadySent = localStorage.getItem(emailSentKey);
    
    if (resolvedOrderId && !alreadySent) {
      console.log('[ElearningConfirmationPage] Vérification si l\'email a déjà été envoyé pour la commande:', resolvedOrderId);
      
      // Marquer l'email comme envoyé immédiatement pour éviter les envois multiples
      localStorage.setItem(emailSentKey, 'true');
      setEmailSent(true);
      
      // Envoyer l'email de confirmation automatiquement
      handleSendEmail(resolvedOrderId);
    } else if (alreadySent) {
      setEmailSent(true);
    }
  }, [orderId]);
  
  // Fonction pour envoyer l'email de confirmation
  const handleSendEmail = async (orderIdToUse?: string) => {
    const targetOrderId = orderIdToUse || orderId || localStorage.getItem('last_elearning_order_id');
    
    if (!targetOrderId) {
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer l'email: ID de commande manquant",
        variant: "destructive"
      });
      return;
    }
    
    setIsSendingEmail(true);
    
    try {
      const result = await sendElearningOrderConfirmationEmail(targetOrderId);
      
      if (result.success) {
        toast({
          title: "Email envoyé",
          description: "L'email de confirmation a été envoyé avec succès",
          variant: "success"
        });
        setEmailSent(true);
        localStorage.setItem(`elearning_email_sent_${targetOrderId}`, 'true');
      } else {
        throw new Error(result.error || "Erreur inconnue");
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email:", error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Une erreur est survenue lors de l'envoi de l'email",
        variant: "destructive"
      });
    } finally {
      setIsSendingEmail(false);
    }
  };
  
  return (
    <PublicLayout>
      <SEOMetaTags 
        title="Confirmation de commande - E-Learning"
        description="Confirmation de votre commande de cours en ligne."
        keywords="confirmation, commande, cours, e-learning, paiement"
      />
      
      <div className="min-h-screen relative overflow-hidden">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {elearningTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: elearningTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[elearningTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                    ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                    ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !elearningTheme.overlay.useGradient
                ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs personnalisables */}
        <div className="absolute inset-0 overflow-hidden z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.08) }} />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.08), animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-3/4 w-48 h-48 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDelay: '4s' }} />
        </div>
        
        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {elearningTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(elearningTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[0].icon] || BookOpen, { size: elearningTheme.icons[0].size })}
            </motion.div>
          )}
          {/* Top-Right */}
          {elearningTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(elearningTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[1].icon] || Sparkles, { size: elearningTheme.icons[1].size })}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {elearningTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(elearningTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[2].icon] || Diamond, { size: elearningTheme.icons[2].size })}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {elearningTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(elearningTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[3].icon] || Star, { size: elearningTheme.icons[3].size })}
            </motion.div>
          )}
        </div>
        
        {/* Contenu de la page */}
        <div className="relative z-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20">
            {/* En-tête de la section - Style Contact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
              className="mb-8 py-16 text-center"
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center bg-white/10 backdrop-blur-sm border rounded-full px-8 py-4 mb-8 shadow-xl hover:shadow-2xl transition-all duration-300"
                style={{
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                }}
              >
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 animate-pulse" />
                <span className="font-semibold text-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Commande confirmée</span>
                <Sparkles className="w-4 h-4 ml-3 animate-bounce" style={{ color: tenant?.config?.color1 || '#fe8d17', animationDelay: '0.5s' }} />
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4, type: "spring", stiffness: 80 }}
                className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg"
                style={{ color: tenant?.config?.color3 || '#ffffff' }}
              >
                Merci pour votre <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>achat</span> !
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-lg drop-shadow-md max-w-3xl mx-auto"
                style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}
              >
                Votre commande a été reçue avec succès. 
                <br />
                <span style={{ color: tenant?.config?.color1 || '#fe8d17' }} className="font-semibold">Vos cours sont maintenant disponibles</span>.
              </motion.p>
            </motion.div>

            {/* Contenu principal */}
            <div className="max-w-4xl mx-auto">
          <motion.div 
            className="space-y-8"
            variants={fadeIn}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card className="bg-white/5 border backdrop-blur-sm overflow-hidden shadow-2xl transition-all duration-500 hover:shadow-3xl text-white"
                style={{
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                }}
              >
                <div className="h-2" style={{ backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})` }}></div>
                <CardContent className="pt-6 pb-8">
                <div className="flex flex-col items-center text-center space-y-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  >
                    <CheckCircle className="h-16 w-16 text-green-500" />
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                  >
                    <h2 className="text-3xl font-bold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Merci pour votre achat !</h2>
                    <p className="text-lg mb-6" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                      Votre commande a été reçue avec succès.
                    </p>
                  </motion.div>
                  
                  <motion.div 
                    className="border rounded-lg p-6 w-full max-w-2xl"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${hexToRgba('#16a34a', 0.1)}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1)})`,
                      borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                    }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                  >
                    <h3 className="font-medium text-xl mb-3" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>Prochaines étapes</h3>
                    <p className="mb-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                      Vos cours sont maintenant disponibles. Pour commencer à apprendre, veuillez vous connecter à votre compte.
                    </p>
                    <ul className="list-disc list-inside space-y-2 mb-6 text-left" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                      <li>Connectez-vous à votre compte avec votre email</li>
                      <li>Accédez à votre tableau de bord étudiant</li>
                      <li>Commencez à suivre vos cours dès maintenant</li>
                    </ul>
                    
                    <div className="flex flex-col sm:flex-row gap-4 mt-2">
                      <Button asChild className="flex-1 shadow-lg transition-all duration-300 font-bold"
                        style={{
                          backgroundColor: tenant?.config?.color1 || '#fe8d17',
                          color: tenant?.config?.color3 || '#ffffff',
                          backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`
                        }}
                      >
                        <Link to="/elearning/student/dashboard">
                          Se connecter pour commencer
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" className="flex-1 transition-all duration-300"
                        style={{
                          borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.3),
                          color: tenant?.config?.color3 || '#ffffff'
                        }}
                      >
                        <Link to="/elearning/courses">
                          Continuer vos achats
                        </Link>
                      </Button>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}>
                      <Button 
                        onClick={() => handleSendEmail()} 
                        disabled={isSendingEmail || emailSent}
                        variant="ghost" 
                        className="w-full flex items-center justify-center gap-2 transition-colors"
                        style={{
                          color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7)
                        }}
                        onMouseEnter={(e) => {
                          if (!isSendingEmail && !emailSent) {
                            e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                            e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSendingEmail && !emailSent) {
                            e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7);
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <Mail className="h-4 w-4" />
                        {isSendingEmail ? 'Envoi en cours...' : emailSent ? 'Email envoyé' : 'Recevoir la confirmation par email'}
                      </Button>
                    </div>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
            </motion.div>
          </motion.div>
          </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ElearningConfirmationPage;
